import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Button, { BUTTON_VARIANTS } from "components/shared/Button/Button";
import { Search } from "components-new";
import { SortBy } from "components/shared";
import AddingCode from "./AddingCode/AddingCode";
import ImportFile from "./ImportFile/ImportFile";
import OkvedTable from "./OkvedTable/OkvedTable";
import useDebounce from "hooks/useDebounce";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import StepInfo from "pages/StepPages/components";
import { useAppDispatch, useAppSelector } from "toolkit/hooks";
import { getCodes, getSections } from "toolkit/codes/codes.thunks";
import { selectRow, setAddingModal, setEditCodeModal, setImportModal, setIsEdit } from "toolkit/codes/codes.slice";
import EditCode from "./EditCode/EditCode";
import Snackbar from "components-new/Snackbar";
import cn from "classnames";
import styles from "./OkvedCodesPage.module.sass";
import { getCodeList } from "toolkit/organizations/organizations.thunks";
import sass from "pages/References/AreasOfActivity/areasActivity.module.sass";
import { getOrganizationsName } from "toolkit/employees/employees.thunks";
import {organizationAPI} from "../../../libs/apiClient/Organization";
import {organizationsAPI} from "../../../libs/apiClient/Organizations";
import {userAPI} from "../../../libs/apiClient/User";

const OkvedCodesPage: FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();
	const navigate = useNavigate();

	const { organizationId } = useParams();

	const { addingCodeModal, editCodeModal, importFileModal } = useAppSelector((state) => state.codes.modals);

	const [organizationName, setOrganizationName] = useState<string | null>(
		useAppSelector((state) => state.organizations.selectedOrganization?.fullName || null),
	);

	const [organizationsName, setOrganizationsName] = useState<any[]>([]);

	const { selectedRows, disabledBtn } = useAppSelector((state) => state.codes);

	const edit = useMemo(() => {
		return /edit$/.test(location.pathname);
	}, [location.pathname]);

	const [searchValue, setSearchValue] = useState<string>("");
	const [currentOrganization, setCurrentOrganization] = useState<{
		name: string;
		text: string;
	}>({ name: "", text: "Все коды" });
	const debouncedValue = useDebounce(searchValue, 400);

	const [file, setFile] = useState<File | null>(null);

	const inputRef = useRef<HTMLInputElement | null>(null);

	const handleCloseAddingModal = () => {
		dispatch(
			setAddingModal({
				visible: false,
				inputValues: { code: "", description: "", name: "", sectionCode: "" },
			}),
		);
	};

	const handleCloseEditModal = () => {
		dispatch(
			setEditCodeModal({
				visible: false,
				inputValues: { code: "", description: "", name: "", sectionCode: "" },
				isMain: false,
			}),
		);
	};

	const handleCloseImportModal = () => {
		dispatch(setImportModal(false));
		setFile(null);
	};

	const importFile = (e: React.ChangeEvent<HTMLInputElement>) => {
		if (e.target.files) setFile(e.target.files[0]);

		dispatch(setImportModal(true));
		e.target.value = "";
		e.target.files = null;
	};

	const onSave = () => {
		organizationAPI.okvedUpdate(+organizationId!, selectedRows).then(() => {
			Snackbar({
				factor: "light",
				title: "Изменения сохранены",
				delay: 3000,
				type: "success",
				position: "top-center",
			});
			dispatch(getCodeList(Number(organizationId)));
		});

		navigate(-1);
	};

	const outlineStyle = useCallback(
		(id: string, isMain: boolean) => {
			return location.state?.outline?.type === "SECTION"
				? isMain && id === location.state?.outline?.id
					? sass.outline
					: ""
				: id === location.state?.outline?.id
				? sass.outline
				: "";
		},
		[location],
	);

	useEffect(() => {
		organizationsAPI.allSimpleList({page:1,num:9999999}).then((data) => {
			const newArr = data.data.map((item: any) => ({
				name: item.id,
				text: item.shortName,
			}));

			newArr.unshift({ name: "", text: "Все коды" });

			setOrganizationsName(newArr);
			setCurrentOrganization({ name: "", text: "Все коды" });
		});
	}, []);

	useEffect(() => {
		dispatch(
			getCodes({
				search: searchValue,
				organizationId: currentOrganization.name,
			}),
		);
		//eslint-disable-next-line
	}, [debouncedValue, currentOrganization]);

	useEffect(() => {
		dispatch(getOrganizationsName());
		dispatch(getSections());

		if (!organizationName && organizationId) {
			organizationAPI.organizationDetail(+organizationId).then((data) => setOrganizationName(data.data?.fullName));
		}

		if (edit) {
			organizationAPI.okvedDetail(+organizationId!).then((data) => {
				dispatch(selectRow(data.data));
			});
		}
		//eslint-disable-next-line
	}, []);

	useEffect(() => {
		dispatch(setIsEdit(edit));
		//eslint-disable-next-line
	}, [location.pathname]);
	const [disabled, setDisabled] = useState(false);
	useEffect(() => {
		userAPI.permissionsCheckCreate(0, ["dictionary.okved.edit"])
			.then((r) => setDisabled(!r.data.length));
	}, []);
	return (
		<div className="step-wrapper">
			{edit ? (
				<div className={cn("step-header", styles.header)}>
					<StepInfo companyName={organizationName || ""} info="Редактировать вид деятельности организации" />

					<div className="step-header__actions">
						<Search
							className={cn(styles.search, "step-header__actions-item")}
							handleSearch={(e) => setSearchValue(e.target.value)}
							searchValue={searchValue}
							placeholder="Найти код, или род деятельности..."
						/>

						<Button className="step-header__actions-item" disabled={disabledBtn} onClick={onSave}>
							Сохранить изменения
						</Button>
					</div>
				</div>
			) : (
				<div className={cn("step-header", styles.header)}>
					<div className="step-header__title">Справочник кодов ОКВЭД</div>
					<div className="step-header__actions">
						<div className={cn(styles.sortBy, "step-header__actions-item")}>
							<SortBy
								options={organizationsName}
								selectedOption={currentOrganization || { name: "", text: "Все коды" }}
								setSelectedOption={(e: any) => setCurrentOrganization(e)}
							/>
						</div>

						<Search
							className={cn(styles.search, "step-header__actions-item")}
							handleSearch={(e) => setSearchValue(e.target.value)}
							searchValue={searchValue}
							placeholder="Найти код, или род деятельности..."
						/>

						<Button
							disabled={disabled}
							className={cn("step-header__actions-item", styles.importBtn)}
							variant={BUTTON_VARIANTS.OUTLINED}
							onClick={() => inputRef.current?.click()}>
							<input
								ref={inputRef}
								type="file"
								className={styles.file}
								onChange={importFile}
								accept={
									".csv,application/vnd.ms-excel,.xlt,application/vnd.ms-excel,.xla,application/vnd.ms-excel,.xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,.xltx,application/vnd.openxmlformats-officedocument.spreadsheetml.template,.xlsm,application/vnd.ms-excel.sheet.macroEnabled.12,.xltm,application/vnd.ms-excel.template.macroEnabled.12,.xlam,application/vnd.ms-excel.addin.macroEnabled.12,.xlsb,application/vnd.ms-excel.sheet.binary.macroEnabled.12"
								}
							/>
							Импорт файла
						</Button>

						<Button
							disabled={disabled}
							className="step-header__actions-item"
							onClick={() =>
								dispatch(
									setAddingModal({
										visible: true,
										inputValues: {
											code: "",
											description: "",
											name: "",
											sectionCode: "",
										},
									}),
								)
							}>
							Добавить значение
						</Button>
					</div>
				</div>
			)}

			<OkvedTable outline={outlineStyle} />

			{addingCodeModal.visible && <AddingCode close={handleCloseAddingModal} />}

			{editCodeModal.visible && <EditCode close={handleCloseEditModal} />}

			{importFileModal && (
				<ImportFile
					close={handleCloseImportModal}
					file={file}
					clearCurrentOrganization={() => setCurrentOrganization({ name: "", text: "Все коды" })}
				/>
			)}
		</div>
	);
};

export default OkvedCodesPage;
